import React, { useState } from 'react'
import Section from './Section'
import { AudioPlayer, RightRail, NavHeader } from './controls'
import { Button } from '../../lib/ui/button'
import { BackArrow } from './icons'
import { ListItems } from './lists'
import ArticleProvider, { useArticle } from './ArticleProvider'
import HighlightCommentProvider from './HighlightCommentProvider'
import TaskCompletedModal from '../shared/TaskCompletedModal'
import useTranslation, {
  TranslationProvider,
} from '../shared/TranslationProvider'
import translations from './locales.json'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const queryClient = new QueryClient()

const ArticleInner = () => {
  const {
    jsonContent: { highlights, highlights_custom_title, heroImage, title },
  } = useArticle()

  return (
    // CTS-9209, CTS-9307, CTS-9292 — Plan 06 visual baseline: article chrome + content
    <div
      className="article-redesign flex w-full flex-col"
      data-testid="resource-center-article"
    >
      <NavHeader />
      <div className="flex w-full flex-row">
        <div className="mb-16 mt-4 flex flex-grow flex-row justify-around font-[Roboto] text-base antialiased md:px-4 lg:pr-0">
          <div className="max-w-auto mx-4 max-w-lg md:max-w-5xl 2xl:m-0">
            <AudioPlayer />
            <div
              role="heading"
              aria-level={1}
              className="mb-7 mt-4 font-roboto-slab text-3.5xl font-normal leading-normal text-intoo-blue-medium"
              data-testid="article-title"
            >
              {title}
            </div>
            {highlights && <Highlights title={highlights_custom_title} />}
            {heroImage && <Hero />}
            <Sections />
          </div>
        </div>
        <RightRail />
      </div>
    </div>
  )
}

const Article = ({ ...props }) => {
  const { locale } = props
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider
        locale={locale}
        translations={translations}
        scope={'javascript.components.Article'}
      >
        <ArticleProvider {...props}>
          <HighlightCommentProvider>
            <ArticleInner />
          </HighlightCommentProvider>
        </ArticleProvider>
      </TranslationProvider>
    </QueryClientProvider>
  )
}

const Highlights = ({ title }) => {
  const { t } = useTranslation('index')
  const {
    jsonContent: { highlights },
  } = useArticle()
  return (
    <div className="mb-10 mt-4 p-8 shadow-[1px_1px_15px_3px_rgba(8,70,124,0.37)]">
      <div>
        <h2 className="!mt-0 mb-4 !text-2xl font-bold text-black">
          {title || t('highlights')}
        </h2>
        <ol className="list-inside list-decimal space-y-4 text-black">
          <HighlightCommentProvider locatorSuffix=".highlights">
            <ListItems items={highlights} locatorSuffix=".highlights" />
          </HighlightCommentProvider>
        </ol>
      </div>
    </div>
  )
}

const Hero = () => {
  const {
    heroAwsUrl,
    jsonContent: {
      heroImage: { title },
    },
  } = useArticle()
  return (
    <div className="mb-10">
      <img
        className="aspect-[11/5] w-full object-cover object-center shadow-md shadow-slate-500/40"
        alt={title}
        src={heroAwsUrl}
      />
    </div>
  )
}

const Sections = () => {
  const {
    jsonContent: { sections },
    sectionRef,
  } = useArticle()

  const lastIndex = sections.length - 1

  return (
    <div ref={sectionRef}>
      {sections.map((section, index) => (
        <div
          key={index}
          className={`scroll-mt-[35px] sm:scroll-mt-[110px] ${
            section.exclude_divider ? 'mb-2' : ''
          }`}
        >
          <HighlightCommentProvider locatorSuffix={`.sections[${index}]`}>
            <Section section={section} />
          </HighlightCommentProvider>
          {index !== lastIndex && !section.exclude_divider && (
            <SectionDivider />
          )}
        </div>
      ))}
      <CompletionNotice />
    </div>
  )
}

const SectionDivider = () => {
  return (
    <div className="my-8 w-full">
      <hr className="w-full border-[1.5px] border-solid border-primary" />
    </div>
  )
}

const CompletionNotice = () => {
  const { t } = useTranslation('')
  const {
    hasNotifiedViewed,
    completionAnimationUrl,
    navigateToResourceCenter,
    jsonContent: { title, guideComplete },
    navigateToGuides,
    cliftonStrengths,
    coachServicesArticle,
    navigateToCoachServices,
    inActionPlan,
    locale,
  } = useArticle()
  const [initialHasNotifiedViewed] = useState(hasNotifiedViewed)
  const showTaskCompletedModal =
    inActionPlan && hasNotifiedViewed && !initialHasNotifiedViewed

  const contentTitle = guideComplete?.title || t('index.congratulations')
  const message =
    guideComplete?.content || t('index.title_marked_complete', { title })
  const className = hasNotifiedViewed ? 'mt-6' : 'hidden'

  return (
    <div className={className}>
      <TaskCompletedModal open={showTaskCompletedModal} locale={locale} />
      <div className="mb-8 flex w-full flex-row items-center">
        <img
          className="w-20"
          src={completionAnimationUrl}
          alt="Article complete"
        />
        <div className="flex flex-col">
          <div className="text-2xl font-bold text-[#27772B]">
            {contentTitle}
          </div>
          <div>{message}</div>
        </div>
      </div>
      <div className="mb-4">
        {cliftonStrengths ? (
          <Button
            variant="default"
            className="h-12 rounded-[1.25rem] py-3 font-medium sm:h-10 sm:py-2"
            onClick={navigateToGuides}
          >
            {t('Controls.back_to_guides')}
            <BackArrow className="ml-2 stroke-white" />
          </Button>
        ) : (
          <Button
            variant="default"
            className="h-12 rounded-[1.25rem] bg-primary py-3 font-medium hover:hover:bg-primary sm:h-10 sm:py-2"
            onClick={
              coachServicesArticle
                ? navigateToCoachServices
                : navigateToResourceCenter
            }
          >
            {coachServicesArticle?.back_to_resource
              ? coachServicesArticle.back_to_resource
              : t('Controls.back_to_resource_center')}
            <BackArrow className="ml-2 stroke-white" />
          </Button>
        )}
      </div>
    </div>
  )
}

export default Article
